import { Alert, CircularProgress } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import SpeedOutlinedIcon from "@mui/icons-material/SpeedOutlined";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { jwtDecode } from "jwt-decode";
import { useEffect, useRef, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { Helmet } from "react-helmet";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import portnetCargoLogo from "../../../assets/images/portnet-cargo-logo.png";
import config from "../../../common/Config";
import { validateAuthentification } from "../../../common/utils/validateUtils";
import axios from "axios";
import {
  authenticate,
  registerMFA,
  sendOtpMail,
} from "../../dfd/state/DfdActions";
import MFAPopup from "./MFAPopup";
import "./Auth.css";

const FEATURE_BADGES = [
  { icon: ShieldOutlinedIcon, label: "Connexion sécurisée" },
  { icon: AccessTimeIcon, label: "Disponibilité 24/24" },
  { icon: SpeedOutlinedIcon, label: "Performance et traçabilité" },
];

const FOOTER_LINKS = [
  // { label: "Mentions légales", href: "https://www.portnet.ma/mentions-legales" },
  // { label: "Confidentialité", href: "https://www.portnet.ma/confidentialite" },
  // { label: "CGU", href: "https://www.portnet.ma/cgu" },
  // { label: "Accessibilité", href: "https://www.portnet.ma/accessibilite" },
];

const Auth = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [showMfaPopup, setShowMfaPopup] = useState(false);
  const [showOtpVerification, setShowOtpVerification] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [codeOTP, setCodeOTP] = useState(null);
  const [username, setUsername] = useState(localStorage.getItem("user") || "");
  const [password, setPassword] = useState(localStorage.getItem("pass") || "");
  const [rememberMe, setRememberMe] = useState(
    !!localStorage.getItem("rememberMe"),
  );
  const { requireOTP, qrCodeUrl, secret, messageMfa } = useSelector(
    (state) => state.authentificationReducer,
  );
  const JToken = useSelector((state) => state.authentificationReducer.token);
  const captchaRef = useRef(null);
  const { enqueueSnackbar } = useSnackbar();
  const [isChecked, setIsChecked] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [showForgotPassword, setShowForgotPassword] = useState(false);

  const error = useSelector((state) => state.authentificationReducer.error);
  const mfaError = useSelector(
    (state) => state.authentificationReducer.mfaErrorMessage,
  );
  const otpMailError = useSelector(
    (state) => state.authentificationReducer.otpMailError,
  );
  const isLoading = useSelector(
    (state) => state.authentificationReducer.isLoading,
  );
  const [errorMessage, setErrorMessage] = useState("");
  const [mfaErrorMessage, setMfaErrorMessage] = useState("");
  const [otpMessage, setOtpMessage] = useState("");
  const [errors, setErrors] = useState({});

  const navigateToPage = (decoded) => {
    let currentHome = "";

    if (
      decoded.roles.includes(
        "ROLE_VUPORTUARIA_MEAD",
        "ROLE_VUPORTUARIA_COMPAGNIE_AERIENNE",
        "ROLE_VUPORTUARIA_ADMIN",
      )
    ) {
      currentHome = "/home";
    }
    navigate(currentHome);
  };

  useEffect(() => {
    if (JToken) {
      const decoded = jwtDecode(JToken);
      if (
        decoded.roles.includes("ROLE_VUPORTUARIA_MEAD") ||
        decoded.roles.includes("ROLE_VUPORTUARIA_COMPAGNIE_AERIENNE") ||
        decoded.roles.includes("ROLE_VUPORTUARIA_ADMIN") ||
        decoded.roles.includes("ROLE_VUPORTUARIA_FREIGHT_FORWARDER") ||
        decoded.roles.includes("ROLE_VUPORTUARIA_OPERADOR")
      ) {
        localStorage.setItem("token", JToken);
        localStorage.setItem("reportCereal", decoded.report_cereal);
        if (rememberMe) {
          localStorage.setItem("user", username);
          localStorage.setItem("pass", password);
          localStorage.setItem("rememberMe", true);
        } else {
          localStorage.removeItem("user");
          localStorage.removeItem("pass");
          localStorage.removeItem("rememberMe");
        }
        navigateToPage(decoded);
      } else {
        setShowOtpVerification(false);
        setShowMfaPopup(false);
        setErrorMessage("nom d'utilisateur et/ou mot de passe incorrecte");
      }
    }
  }, [JToken]);

  const handleMailClick = () => {
    setErrorMessage("");
    const payload = {
      username,
      password,
      otp: codeOTP,
      secret,
    };
    dispatch(sendOtpMail(payload));
  };

  const handleRegisterClick = () => {
    setMfaErrorMessage("");
    const payload = {
      enableMailMfa: true,
      username,
      password,
      otp: codeOTP,
      secret,
    };
    dispatch(registerMFA(payload));
    setCodeOTP(null);
  };

  const handleLoginClick = () => {
    setErrorMessage("");
    const { isValid, errors: validationErrors } = validateAuthentification({
      username,
      password,
    });
    setErrors(validationErrors);
    if (isValid) {
      if (!showOtpVerification && !isChecked) {
        showErrorSnackbar("Veuillez cocher la case 'Je ne suis pas un robot'.");
        return;
      }

      const payload = {
        username,
        password,
        otp: codeOTP,
        ...(showOtpVerification ? {} : { captchaToken }),
      };

      dispatch(authenticate(payload));

      if (!showOtpVerification) {
        captchaRef.current.reset();
        setIsChecked(false);
      }

      setCodeOTP(null);
    }
  };

  const onCaptchaChange = (value) => {
    setCaptchaToken(value);
    setIsChecked(!!value);
  };

  const showErrorSnackbar = (message) => {
    enqueueSnackbar(message, {
      variant: "error",
    });
  };

  useEffect(() => {
    messageMfa && setOtpMessage(messageMfa);
  }, [messageMfa]);

  useEffect(() => {
    requireOTP === "true" && setShowOtpVerification(true);
  }, [requireOTP]);

  useEffect(() => {
    qrCodeUrl && setShowMfaPopup(true);
  }, [qrCodeUrl]);

  const handleEnterKeyPress = (e) => {
    if (e.key === "Enter") {
      handleLoginClick();
    }
  };

  useEffect(() => {
    if (error !== null && error !== "") {
      const errorString = error.toString();
      if (errorString.includes("401") || errorString.includes("500")) {
        if (showOtpVerification) {
          setErrorMessage("Code OTP incorrect. Veuillez réessayer.");
          setCodeOTP(null);
        } else {
          setErrorMessage("Nom d'utilisateur et/ou mot de passe incorrecte");
          setCodeOTP(null);
        }
      } else {
        setErrorMessage(
          "Erreur d'authentification, veuillez réessayer ultérieurement.",
        );
      }
    }
  }, [error, showOtpVerification]);

  useEffect(() => {
    if (mfaError !== null && mfaError !== "") {
      const errorString = mfaError.toString();
      if (errorString.includes("401") || errorString.includes("500")) {
        setMfaErrorMessage("Code OTP incorrect. Veuillez réessayer.");
      } else {
        setMfaErrorMessage("Erreur d'enregistrement MFA. Veuillez réessayer.");
      }
      setCodeOTP(null);
    }
  }, [mfaError]);

  useEffect(() => {
    if (otpMailError !== null && otpMailError !== "") {
      setErrorMessage(
        "Erreur lors de l'envoi du code OTP. Veuillez réessayer.",
      );
    }
  }, [otpMailError]);

  const handleForgotPassword = () => {
    setErrorMessage("");
    setErrors({});
    setUsername("");
    setShowForgotPassword(true);
  };

  const handleBackToLogin = () => {
    setErrorMessage("");
    setUsername("");
    setShowForgotPassword(false);
  };

  const handleResetRequest = async () => {
    setErrorMessage("");

    const login = username.trim();
    if (!login) {
      setErrorMessage("Veuillez saisir votre identifiant.");
      return;
    }

    try {
      const response = await axios.post(
        `${config.crcUrl}/api/user/resetPassword`,
        { username: login, portalName: "Portail Cargo" },
      );

      enqueueSnackbar(
        response?.data?.message ||
          "Si un compte existe, un lien de réinitialisation a été envoyé.",
        { variant: "success" },
      );
      setUsername("");
      setShowForgotPassword(false);
    } catch (error) {
      enqueueSnackbar(
        "Une erreur s'est produite. Veuillez réessayer ultérieurement.",
        { variant: "error" },
      );
    }
  };

  return (
    <div className="auth-page">
      <Helmet>
        <title>Connexion - Portail Mead &amp; Aerien</title>
      </Helmet>

      {isLoading && (
        <div className="auth-loading-overlay">
          <CircularProgress sx={{ color: "#ffffff" }} />
        </div>
      )}

      <div className="auth-container">
        <aside className="auth-left-panel">
          <div className="auth-pre-align">
            <div className="auth-logo-container">
              <img
                src={portnetCargoLogo}
                alt="PORTNET CARGO"
                className="auth-logo"
              />
            </div>
            <div className="auth-section-label">
              Espace opérateur économique
            </div>
          </div>
          <div className="auth-align-block">
            <h1 className="auth-headline">
              Simplifiez et Pilotez votre{" "}
              <span className="auth-headline-underline">
                <i>Logistique</i>
              </span>
            </h1>
            <p className="auth-description">
              PORTNET CCS est la plateforme communautaire dédiée au traitement
              du fret et à la gestion des magasins et aires de dédouanement
              (MEAD).
            </p>
            <div className="auth-badges">
              {FEATURE_BADGES.map(({ icon: Icon, label }) => (
                <span key={label} className="auth-badge">
                  <Icon fontSize="small" />
                  {label}
                </span>
              ))}
            </div>
          </div>
        </aside>

        <div className="auth-right-column">
          <main className="auth-right-panel">
            <div className="auth-form-wrapper">
              <div className="auth-pre-align">
                {showForgotPassword ? (
                  <>
                    <div className="auth-section-label auth-section-label--red">
                      Mot de passe oublié
                    </div>
                    <h2 className="auth-form-title">
                      Réinitialiser mon mot de passe
                    </h2>
                    <p className="auth-form-subtitle">
                      Saisissez votre identifiant pour recevoir un lien de
                      réinitialisation.
                    </p>
                  </>
                ) : (
                  <>
                    <div className="auth-section-label auth-section-label--red">
                      Connexion
                    </div>
                    <h2 className="auth-form-title">Accéder à mon espace</h2>
                    <p className="auth-form-subtitle">
                      Renseignez vos identifiants opérateur pour piloter vos
                      opérations de commerce extérieur.
                    </p>
                  </>
                )}

                {errorMessage && (
                  <Alert className="auth-alert" severity="error">
                    {errorMessage}
                  </Alert>
                )}
              </div>

              <div className="auth-align-block">
                {showForgotPassword ? (
                  <>
                    <br />
                    <div className="auth-field-group">
                      <label
                        className="auth-field-label"
                        htmlFor="auth-reset-username"
                      >
                        Identifiant
                      </label>
                      <input
                        id="auth-reset-username"
                        className="auth-field-input"
                        placeholder="nom d'utilisateur"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") handleResetRequest();
                        }}
                        autoComplete="username"
                      />
                    </div>

                    <button
                      type="button"
                      className="auth-submit-btn"
                      style={{ marginTop: "1.5em" }}
                      onClick={handleResetRequest}
                    >
                      Soumettre
                      <ArrowForwardIcon fontSize="small" />
                    </button>

                    <button
                      type="button"
                      className="auth-back-btn"
                      onClick={handleBackToLogin}
                    >
                      <ArrowBackIcon fontSize="small" />
                      Retour à la connexion
                    </button>
                  </>
                ) : (
                  <>
                    {showOtpVerification ? (
                      <>
                        <p className="auth-otp-message">
                          {otpMessage ||
                            "Veuillez saisir le code à 6 chiffres affiché sur votre application mobile."}
                        </p>
                        <div className="auth-field-group">
                          <label
                            className="auth-field-label"
                            htmlFor="auth-otp"
                          >
                            Code OTP
                          </label>
                          <input
                            id="auth-otp"
                            className={`auth-field-input${
                              errors.codeOTP ? " auth-field-input--error" : ""
                            }`}
                            placeholder="••••••"
                            value={codeOTP || ""}
                            maxLength={6}
                            onChange={(e) => setCodeOTP(e.target.value)}
                            onKeyDown={handleEnterKeyPress}
                          />
                          {errors.codeOTP && (
                            <span className="auth-field-error">
                              {errors.codeOTP}
                            </span>
                          )}
                        </div>
                        <button
                          type="button"
                          className="auth-otp-mail-link"
                          onClick={handleMailClick}
                        >
                          Envoyer le code par mail
                        </button>
                      </>
                    ) : (
                      <>
                        <br></br>
                        <div className="auth-field-group">
                          <label
                            className="auth-field-label"
                            htmlFor="auth-username"
                          >
                            Identifiant
                          </label>
                          <input
                            id="auth-username"
                            className={`auth-field-input${
                              errors.username ? " auth-field-input--error" : ""
                            }`}
                            placeholder="nom d'utilisateur"
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            onKeyDown={handleEnterKeyPress}
                            autoComplete="username"
                          />
                          {errors.username && (
                            <span className="auth-field-error">
                              {errors.username}
                            </span>
                          )}
                        </div>

                        <div className="auth-field-group">
                          <label
                            className="auth-field-label"
                            htmlFor="auth-password"
                          >
                            Mot de passe
                          </label>
                          <div className="auth-password-wrapper">
                            <input
                              id="auth-password"
                              className={`auth-field-input auth-field-input--password${
                                errors.password
                                  ? " auth-field-input--error"
                                  : ""
                              }`}
                              type={showPassword ? "text" : "password"}
                              placeholder="••••••••"
                              value={password}
                              onChange={(e) => setPassword(e.target.value)}
                              onKeyDown={handleEnterKeyPress}
                              autoComplete="current-password"
                            />
                            <button
                              type="button"
                              className="auth-password-toggle"
                              onClick={() => setShowPassword((prev) => !prev)}
                              aria-label="Afficher ou masquer le mot de passe"
                            >
                              {showPassword ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </button>
                          </div>
                          {errors.password && (
                            <span className="auth-field-error">
                              {errors.password}
                            </span>
                          )}
                        </div>
                      </>
                    )}

                    <div
                      className="auth-form-options"
                      style={{ marginTop: "1.5em" }}
                    >
                      <label className="auth-remember-me">
                        <input
                          type="checkbox"
                          checked={rememberMe}
                          onChange={(e) => setRememberMe(e.target.checked)}
                        />
                        Se souvenir de moi
                      </label>

                      <button
                        type="button"
                        className="auth-forgot-link"
                        onClick={handleForgotPassword}
                      >
                        Mot de passe oublié ?
                      </button>
                    </div>

                    {!showOtpVerification && (
                      <div
                        className="auth-captcha"
                        style={{ marginTop: "1.25em" }}
                      >
                        <ReCAPTCHA
                          ref={captchaRef}
                          sitekey={config.captchaSiteKey}
                          onChange={onCaptchaChange}
                        />
                      </div>
                    )}

                    <button
                      type="button"
                      className="auth-submit-btn"
                      onClick={handleLoginClick}
                    >
                      Se connecter
                      <ArrowForwardIcon fontSize="small" />
                    </button>
                  </>
                )}
              </div>
            </div>
          </main>

          <footer className="auth-footer">
            <span>© 2026 PORTNET S.A.</span>
            <nav className="auth-footer-links" aria-label="Liens légaux">
              {FOOTER_LINKS.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {link.label}
                </a>
              ))}
            </nav>
          </footer>
        </div>
      </div>

      <MFAPopup
        open={showMfaPopup}
        onClose={() => {
          setShowMfaPopup(false);
          setMfaErrorMessage("");
        }}
        qrCodeBase64={qrCodeUrl}
        secretCode={secret}
        codeOTP={codeOTP}
        setCodeOTP={setCodeOTP}
        handleRegisterClick={handleRegisterClick}
        mfaErrorMessage={mfaErrorMessage}
      />
    </div>
  );
};

export default Auth;
